import './dashboard.css';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setPageTitle } from 'store/actions/header';
import { useCampaignService } from 'hooks/api/mailing/campaign';
import { GridColumn, GridRow } from 'app/member/layouts/grid';
import { BasicInfoSection } from 'app/shared/info-section/basic';
import { DetailItem } from 'app/shared/info-section/item';
import { useAudienceService } from 'hooks/api/users/audience';
import { formatDateForDisplay } from 'app/shared/utils/date';
import { useContactService } from 'hooks/api/users/contact';
import ContextMenu from 'app/shared/datatable/context-menu/context-menu';
import { MoreButton } from 'app/shared/button/more';
import { Spacer } from 'app/member/layouts/generic';

export const CampaignInformation = ({ id }) => {
  const dispatch = useDispatch();
  const { fetchAudiences } = useAudienceService();
  const { fetchAudienceContacts } = useContactService();
  const { fetchCampaignById, requeueCampaign } = useCampaignService();

  const [audiences, setAudiences] = useState([]);
  const [campaign, setCampaign] = useState({});
  const [eligible_contacts, setEligibleContacts] = useState(0);

  const campaign_actions = [[{ label: 'Requeue', value: 'requeue' }]];

  useEffect(() => {
    fetchCampaignById(id).then(async ({ campaign }) => {
      const { mailing_lists } = campaign;
      setCampaign(campaign);
      dispatch(
        setPageTitle([
          { title: 'My Campaigns', path: '.' },
          { title: campaign.name, path: `/campaigns/${id}` }
        ])
      );

      const [{ audiences: audience_from_db }, { size: eligible_contacts }] = await Promise.all([
        fetchAudiences({
          query_string: `id=${mailing_lists.join(',')}`
        }),
        fetchAudienceContacts(mailing_lists.join(','), {
          query_string: `tenant_id=${campaign.tenant_id}&bool=is_valid,-is_unsubscribed&count=1`
        })
      ]);

      setAudiences(audience_from_db);
      setEligibleContacts(eligible_contacts);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContextAction = (action) => {
    if (action === 'requeue') handleCampaignRequeue(id);
  };

  const handleCampaignRequeue = async () => {
    const data = {
      metadata: { ...campaign.metadata, retry_count: (campaign.metadata?.retry_count || 0) + 1 },
      status: 'queued',
      is_enqueued: false
    };
    const result = await requeueCampaign(id, { data });
    if (result) window.location.reload();
  };

  return (
    <GridRow>
      <GridColumn span={3}>
        <iframe
          title="Campaign body"
          srcDoc={campaign.body || ''}
          style={{
            width: '100%',
            minHeight: '75vh',
            padding: '15px',
            background: 'var(--neutral-light)',
            border: '1px solid var(--neutral-dark-5)',
            borderRadius: '8px'
          }}
        ></iframe>
      </GridColumn>
      <GridColumn span={1}>
        <ContextMenu
          actions={campaign_actions}
          callback={handleContextAction}
          text={<MoreButton text="Actions" />}
        />
        <Spacer multiple={4} />
        <BasicInfoSection title="Basic Details">
          <DetailItem title="Name:">{campaign.name || 'N/A'}</DetailItem>
          <DetailItem title="Subject:">{campaign.subject || 'N/A'}</DetailItem>
          <DetailItem title="Sender Name:">{campaign.sender_name || 'N/A'}</DetailItem>
          <DetailItem title="Sender Email:">{campaign.sender_email || 'N/A'}</DetailItem>
          <DetailItem title="Preview Header:">{campaign.preview_text || 'N/A'}</DetailItem>
          <DetailItem title="Scheduled date:">
            {formatDateForDisplay(campaign.schedule?.date) || 'N/A'}
          </DetailItem>
          <DetailItem title="Status:">{campaign.status || 'N/A'}</DetailItem>
          <DetailItem title="Audiences">
            {audiences.map((audience) => (
              <p key={audience.id}>{audience.name}</p>
            ))}
          </DetailItem>
          <DetailItem title="Eligible Contacts">{eligible_contacts}</DetailItem>
          <DetailItem title="Number of Retries">
            {campaign.metadata?.retry_count + 1 || 0}
          </DetailItem>
        </BasicInfoSection>
      </GridColumn>
    </GridRow>
  );
};
